import { useState, useEffect, useCallback } from 'react'

/**
 * Hook for managing offline capabilities
 */
export const useOffline = () => {
  const [isOnline, setIsOnline] = useState(navigator.onLine)
  const [wasOffline, setWasOffline] = useState(false)

  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true)
      if (wasOffline) {
        setWasOffline(false)
        // Trigger sync when coming back online
        window.dispatchEvent(new CustomEvent('sync-offline-data'))
      }
    }

    const handleOffline = () => {
      setIsOnline(false)
      setWasOffline(true)
    }

    window.addEventListener('online', handleOnline)
    window.addEventListener('offline', handleOffline)

    return () => {
      window.removeEventListener('online', handleOnline)
      window.removeEventListener('offline', handleOffline)
    }
  }, [wasOffline])

  return { isOnline, wasOffline }
}

/**
 * Hook for local storage management
 */
export const useLocalStorage = (key, initialValue) => {
  const [storedValue, setStoredValue] = useState(() => {
    try {
      const item = window.localStorage.getItem(key)
      return item ? JSON.parse(item) : initialValue
    } catch (error) {
      console.error(`Error reading localStorage key "${key}":`, error)
      return initialValue
    }
  })

  const setValue = useCallback((value) => {
    try {
      const valueToStore = value instanceof Function ? value(storedValue) : value
      setStoredValue(valueToStore)
      window.localStorage.setItem(key, JSON.stringify(valueToStore))
    } catch (error) {
      console.error(`Error setting localStorage key "${key}":`, error)
    }
  }, [key, storedValue])

  const removeValue = useCallback(() => {
    try {
      window.localStorage.removeItem(key)
      setStoredValue(initialValue)
    } catch (error) {
      console.error(`Error removing localStorage key "${key}":`, error)
    }
  }, [key, initialValue])

  return [storedValue, setValue, removeValue]
}

/**
 * Hook for offline test progress management
 */
export const useOfflineTestProgress = () => {
  const [testProgress, setTestProgress, removeTestProgress] = useLocalStorage('test_progress', null)
  const [offlineAnswers, setOfflineAnswers, removeOfflineAnswers] = useLocalStorage('offline_answers', {})
  const [offlineQuestions, setOfflineQuestions, removeOfflineQuestions] = useLocalStorage('offline_questions', [])

  const saveTestProgress = useCallback((progress) => {
    const progressData = {
      ...progress,
      timestamp: Date.now(),
      isOffline: !navigator.onLine
    }
    setTestProgress(progressData)
  }, [setTestProgress])

  const saveAnswer = useCallback((questionId, answer) => {
    setOfflineAnswers(prev => ({
      ...prev,
      [questionId]: {
        answer,
        timestamp: Date.now()
      }
    }))
  }, [setOfflineAnswers])

  const saveQuestions = useCallback((questions) => {
    setOfflineQuestions(questions)
  }, [setOfflineQuestions])

  const clearOfflineData = useCallback(() => {
    removeTestProgress()
    removeOfflineAnswers()
    removeOfflineQuestions()
  }, [removeTestProgress, removeOfflineAnswers, removeOfflineQuestions])

  const hasOfflineData = Boolean(testProgress || Object.keys(offlineAnswers).length > 0)

  return {
    testProgress,
    offlineAnswers,
    offlineQuestions,
    saveTestProgress,
    saveAnswer,
    saveQuestions,
    clearOfflineData,
    hasOfflineData
  }
}

/**
 * Hook for network error handling
 */
export const useNetworkError = () => {
  const [networkError, setNetworkError] = useState(null)
  const [retryCount, setRetryCount] = useState(0)

  const handleNetworkError = useCallback((error) => {
    setNetworkError(error)
    setRetryCount(prev => prev + 1)
  }, [])

  const clearNetworkError = useCallback(() => {
    setNetworkError(null)
    setRetryCount(0)
  }, [])

  const retryRequest = useCallback(async (requestFn) => {
    try {
      clearNetworkError()
      const result = await requestFn()
      return result
    } catch (error) {
      handleNetworkError(error)
      throw error
    }
  }, [clearNetworkError, handleNetworkError])

  return {
    networkError,
    retryCount,
    handleNetworkError,
    clearNetworkError,
    retryRequest
  }
}

/**
 * Hook for offline queue management
 */
export const useOfflineQueue = () => {
  const [queue, setQueue, removeQueue] = useLocalStorage('offline_queue', [])

  const addToQueue = useCallback((action) => {
    const queueItem = {
      id: Date.now().toString(),
      action,
      timestamp: Date.now(),
      retryCount: 0
    }
    setQueue(prev => [...prev, queueItem])
  }, [setQueue])

  const removeFromQueue = useCallback((id) => {
    setQueue(prev => prev.filter(item => item.id !== id))
  }, [setQueue])

  const processQueue = useCallback(async () => {
    if (!navigator.onLine || queue.length === 0) return

    const processedItems = []
    
    for (const item of queue) {
      try {
        // Process the queued action
        await item.action()
        processedItems.push(item.id)
      } catch (error) {
        console.error('Failed to process queued item:', error)
        // Increment retry count
        setQueue(prev => prev.map(queueItem => 
          queueItem.id === item.id 
            ? { ...queueItem, retryCount: queueItem.retryCount + 1 }
            : queueItem
        ))
      }
    }

    // Remove successfully processed items
    processedItems.forEach(id => removeFromQueue(id))
  }, [queue, setQueue, removeFromQueue])

  const clearQueue = useCallback(() => {
    removeQueue()
  }, [removeQueue])

  return {
    queue,
    addToQueue,
    removeFromQueue,
    processQueue,
    clearQueue
  }
}

/**
 * Hook for caching API responses
 */
export const useApiCache = (cacheKey) => {
  const [cache, setCache, removeCache] = useLocalStorage(`api_cache_${cacheKey}`, {})

  const getCachedData = useCallback((key) => {
    const cached = cache[key]
    if (!cached) return null

    // Check if cache is expired (24 hours)
    const isExpired = Date.now() - cached.timestamp > 24 * 60 * 60 * 1000
    if (isExpired) {
      return null
    }

    return cached.data
  }, [cache])

  const setCachedData = useCallback((key, data) => {
    setCache(prev => ({
      ...prev,
      [key]: {
        data,
        timestamp: Date.now()
      }
    }))
  }, [setCache])

  const clearCache = useCallback(() => {
    removeCache()
  }, [removeCache])

  return {
    getCachedData,
    setCachedData,
    clearCache
  }
}